import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "../../components/common/button";
import Loader from "../../components/common/loader";
import { getPaymentStatus } from "../../service/payment/paymentService";
import { formatAmount, rmEmail, rmNumber } from "../../utils/helper";
import { paymentFailed, paymentSuccess } from "../../utils/router/constant";
import "./index.scss";

const POLL_INTERVAL_MS = 500;
const GLOBAL_TIMEOUT_MS = 30000;

const PAYMENT_STATUS = {
  success: "Success",
  failed: "Failed",
};

const PHASE = {
  polling: "polling",
  missingOrderId: "missingOrderId",
  notFound: "notFound",
  timedOut: "timedOut",
};

const toRouteState = (data) => {
  let paymentDate = data.paymentDate ?? data.payment_date;
  if (data.payment_response) {
    try {
      const parsed = JSON.parse(data.payment_response);
      const time = parsed?.data?.payment?.payment_time ?? parsed?.payment_time ?? parsed?.payment_completion_time;
      if (time) {
        paymentDate = time;
      }
    } catch (e) {
      console.error("Error parsing payment_response for date", e);
    }
  }

  return {
    orderId: data.orderId ?? data.order_id,
    amount: data.amount,
    mode: data.mode,
    txnId: data.txnId ?? data.txn_id,
    bankRrn: data.bankRrn ?? data.bank_rrn,
    paymentDate,
    paymentStatus: data.paymentStatus ?? data.payment_status,
    paymentgateway: data.paymentgateway ?? data.payment_gateway,
    errorCode: data.errorCode ?? data.error_code,
    errorMessage: data.errorMessage ?? data.error_message,
  };
};

const PaymentProcessing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");

  const [phase, setPhase] = useState(
    orderId ? PHASE.polling : PHASE.missingOrderId,
  );
  const [amount, setAmount] = useState(null);

  const stoppedRef = useRef(false);
  const timerRef = useRef(null);

  // Cashfree lands the browser here straight from checkout. Going back would
  // re-open the abandoned checkout page, so neutralise it for as long as we
  // are on this route; the result screens replace this entry on the way out.
  useEffect(() => {
    const pinHistory = () =>
      window.history.pushState(null, "", window.location.href);

    pinHistory();
    window.addEventListener("popstate", pinHistory);
    return () => window.removeEventListener("popstate", pinHistory);
  }, []);

  const stopPolling = useCallback(() => {
    stoppedRef.current = true;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (!orderId) return;

    stoppedRef.current = false;
    const deadline = Date.now() + GLOBAL_TIMEOUT_MS;

    const poll = async () => {
      if (stoppedRef.current) return;

      try {
        const data = await getPaymentStatus(orderId);
        if (stoppedRef.current) return;

        if (data?.amount != null) {
          setAmount(data.amount);
        }

        const rawStatus = data?.paymentStatus ?? data?.payment_status;
        const normalizedStatus = typeof rawStatus === "string" ? rawStatus.toLowerCase() : "";

        if (normalizedStatus === "success") {
          stopPolling();
          navigate(paymentSuccess, {
            replace: true,
            state: toRouteState(data),
          });
          return;
        }

        if (normalizedStatus === "failed") {
          stopPolling();
          navigate(paymentFailed, { replace: true, state: toRouteState(data) });
          return;
        }
      } catch (error) {
        if (stoppedRef.current) return;

        if (error?.response?.status === 404) {
          stopPolling();
          setPhase(PHASE.notFound);
          return;
        }
        // 5xx and network failures mean "ask again", never "payment failed" —
        // the webhook is the only writer of a terminal status.
      }

      if (Date.now() >= deadline) {
        stopPolling();
        setPhase(PHASE.timedOut);
        return;
      }

      timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
  }, [navigate, orderId, stopPolling]);

  useEffect(() => {
    if (!orderId) return undefined;

    startPolling();
    return stopPolling;
  }, [orderId, startPolling, stopPolling]);

  const handleRefresh = () => {
    setPhase(PHASE.polling);
    startPolling();
  };

  const handleContactSupport = () => {
    const subject = orderId
      ? `Payment status help - Order ${orderId}`
      : "Payment status help";
    const body = orderId
      ? `I need help with my payment.\n\nOrder ID: ${orderId}`
      : "I need help with my payment.";

    window.location.href = `mailto:${rmEmail}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  };

  const renderOrderId = () =>
    orderId ? (
      <div className="payment-processing--order-id">Order ID: {orderId}</div>
    ) : null;

  const renderSupportNote = () => (
    <p className="payment-processing--support">
      Need help? Email {rmEmail} or call {rmNumber}
      {orderId ? `, quoting order ID ${orderId}.` : "."}
    </p>
  );

  const renderContent = () => {
    switch (phase) {
      case PHASE.missingOrderId:
        return (
          <>
            <h1 className="payment-processing--title error">
              Something went wrong
            </h1>
            <p className="payment-processing--message">
              We could not find a payment reference in this link, so there is
              nothing to confirm. If money left your account, contact us and we
              will trace it.
            </p>
            {renderSupportNote()}
            <div className="payment-processing--actions">
              <Button
                type="button"
                variant="secondary"
                label="Contact support"
                onClick={handleContactSupport}
              />
            </div>
          </>
        );

      case PHASE.notFound:
        return (
          <>
            <h1 className="payment-processing--title error">
              Payment record not found
            </h1>
            <p className="payment-processing--message">
              We have no record of this payment. If money left your account,
              contact us and we will trace it.
            </p>
            {renderOrderId()}
            {renderSupportNote()}
            <div className="payment-processing--actions">
              <Button
                type="button"
                variant="secondary"
                label="Contact support"
                onClick={handleContactSupport}
              />
            </div>
          </>
        );

      case PHASE.timedOut:
        return (
          <>
            <h1 className="payment-processing--title">
              This is taking longer than usual
            </h1>
            <p className="payment-processing--message">
              We have not received a confirmation yet. Your payment has not
              failed &mdash; refresh to check again, or contact us and we will
              look it up for you.
            </p>
            {renderOrderId()}
            {renderSupportNote()}
            <div className="payment-processing--actions">
              <Button
                type="button"
                variant="primary"
                label="Refresh"
                onClick={handleRefresh}
              />
              <Button
                type="button"
                variant="secondary"
                label="Contact support"
                onClick={handleContactSupport}
              />
            </div>
          </>
        );

      default:
        return (
          <>
            <Loader height={56} width={56} />
            <h1 className="payment-processing--title">
              Confirming your payment&hellip;
            </h1>
            {amount != null && (
              <div className="payment-processing--amount">
                {formatAmount(amount, null, null, null)}
              </div>
            )}
            <p className="payment-processing--message">
              Please do not close or refresh this window.
            </p>
            {renderOrderId()}
          </>
        );
    }
  };

  return (
    <div className="payment-processing--container">
      <div className="payment-processing--card">{renderContent()}</div>
    </div>
  );
};

export default PaymentProcessing;
